import {
  SelectPositionByDepartmentDataType,
  SelectPositionByDepartmentOptionType,
} from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.types';
import { useSSFBehaviors } from 'hooks';
import { QUERY_positionByDepartmentSelectInput } from 'queries';
import { createContext, FC, useCallback, useMemo } from 'react';
import { GQLAPIContextType, pageContextInitialState } from 'types/common.types';
import {
  GroupedPositionsConnection,
  PositionByDepartmentSelectInputQuery,
  PositionDetails,
} from 'types/graphql';
import { nonPaginatedApolloClient } from 'providers/ApolloClients/NonPaginatedApolloClient';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { useSelector } from 'react-redux';

export const SelectInputPositionByDepartmentContext = createContext(
  pageContextInitialState,
) as GQLAPIContextType;

const client = nonPaginatedApolloClient();

const capitalize = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || '';
const snakeToTitleCase = (s: string) => s.split('_').map(capitalize).join(' ');

export const SelectInputPositionByDepartmentProvider: FC<{ allocatedToCommunities?: string[] }> = ({
  allocatedToCommunities = [],
  children,
}) => {
  const isUserAgency = useSelector(isUserAgencySelector);
  const [values, useQueryOptions] = useSSFBehaviors({
    key: 'POSITION_BY_FACILITY_FILTER',
    query: QUERY_positionByDepartmentSelectInput,
    client,
    payloadTransformer: (variables) => {
      const payload = {
        ...variables,
        filter: {
          byName: variables.byName,
          byAgencyCommunity: undefined,
        },
      };
      if (isUserAgency) {
        payload.filter.byAgencyCommunity = allocatedToCommunities.map((communityId) =>
          Number(communityId),
        );
      } else {
        payload.filter.allocatedToCommunities = allocatedToCommunities.map((communityId) =>
          Number(communityId),
        );
      }
      return payload;
    },
    responseTransformer: (
      data: PositionByDepartmentSelectInputQuery,
    ): SelectPositionByDepartmentDataType => {
      if (!data) return {};
      if (!data.groupPositions) return {};

      const { edges } = data?.groupPositions as GroupedPositionsConnection;
      const filteredEdges = edges?.filter((edge) => !!edge?.node) as unknown as {
        node: { group: string; positions: PositionDetails[] };
      }[];

      let options: Record<string, SelectPositionByDepartmentOptionType[]> = {};
      filteredEdges.forEach(({ node: { positions, group } }) => {
        if (positions) {
          options[snakeToTitleCase(group)] = positions.map((position) => {
            return {
              value: `${position.id}`,
              label: position.name,
              key: position.id.toString(),
            };
          });
        }
      });
      return options;
    },
    schema: {
      byFullName: {
        initialValue: '',
        mapper: String,
      },
      ...(isUserAgency
        ? {
            byAgencyCommunity: {
              initialValue: allocatedToCommunities,
            },
          }
        : {
            allocatedToCommunities: {
              initialValue: allocatedToCommunities,
            },
          }),
    },
  });

  const onClose = useCallback(() => {
    useQueryOptions.onChange({
      target: { name: 'byName', value: '' },
    });
  }, [useQueryOptions]);

  const value = useMemo(() => {
    return { onClose, values, ...useQueryOptions };
  }, [onClose, values, useQueryOptions]);

  return (
    <SelectInputPositionByDepartmentContext.Provider value={value}>
      {children}
    </SelectInputPositionByDepartmentContext.Provider>
  );
};
